import { CaretRight, DotsThree } from "@phosphor-icons/react"
import {
  Breadcrumbs as AriaBreadcrumbs,
  Breadcrumb as AriaBreadcrumb,
  type BreadcrumbProps as AriaBreadcrumbProps,
  type BreadcrumbsProps as AriaBreadcrumbsProps,
  Link,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { Icon } from "../../icon"

type BreadcrumbProps = AriaBreadcrumbProps & {
  /** A link to navigate to when the breadcrumb is clicked. */
  href?: string
}

/**
 * An ellipsis icon that represents a collapsed set of breadcrumbs. Should be used when there are too many breadcrumbs to display.
 */

export const BreadcrumbEllipsis = () => {
  return (
    <AriaBreadcrumb className="tt-breadcrumb-icon-container">
      <Icon
        as={DotsThree}
        width={16}
        weight="bold"
        className="tt-separator-icon"
      />
    </AriaBreadcrumb>
  )
}

/**
 * A separator between breadcrumbs. Should be used between `Breadcrumb` components.
 */

export const BreadcrumbSeparator = () => {
  return (
    <AriaBreadcrumb className="tt-breadcrumb-icon-container">
      <Icon
        as={CaretRight}
        width={12}
        weight="bold"
        className="tt-separator-icon"
      />
    </AriaBreadcrumb>
  )
}

/**
 * A single item in a set of breadcrumbs. For a full list of props, see react-aria-components [Breadcrumb](https://react-spectrum.adobe.com/react-aria/Breadcrumb.html#breadcrumb).
 */

export const Breadcrumb = ({
  children,
  href,
  className,
  ...props
}: BreadcrumbProps) => {
  const text: BreadcrumbProps["children"] = (args) => (
    // TODO: Fix size prop
    <span className="tt-breadcrumb">
      {typeof children === "function" ? children(args) : children}
    </span>
  )

  return (
    <AriaBreadcrumb className={className} {...props}>
      {(args) =>
        href ? (
          <Link href={href} className="tt-breadcrumb-link">
            {text(args)}
          </Link>
        ) : (
          text(args)
        )
      }
    </AriaBreadcrumb>
  )
}

type BreadcrumbsProps = AriaBreadcrumbsProps<object> & {
  /** An array of `Breadcrumb`, `BreadcrumbSeparator`, or `BreadcrumbEllipsis` components. */
  children: AriaBreadcrumbProps["children"]
}

/** A set of components that represent a navigation path. For a full list of props, see react-aria-components [Breadcrumbs](https://react-spectrum.adobe.com/react-aria/Breadcrumbs.html#breadcrumbs-1).
 *
 * ```tsx
 * import { Breadcrumbs, Breadcrumb, BreadcrumbSeparator, BreadcrumbEllipsis } from "@tokenterminal/core/ui/breadcrumbs/Breadcrumbs"
 *
 * <Breadcrumbs>
 *   <Breadcrumb href="/studio">Studio</Breadcrumb>
 *   <BreadcrumbSeparator />
 *   <Breadcrumb href="/studio/charts/">Charts</Breadcrumb>
 *   <BreadcrumbSeparator />
 *   <Breadcrumb>New chart</Breadcrumb>
 * </Breadcrumbs>
 * ```
 */

export const Breadcrumbs = ({ className, ...props }: BreadcrumbsProps) => {
  return (
    <AriaBreadcrumbs
      className={twMerge("tt-breadcrumbs-container", className)}
      {...props}
    />
  )
}
